import { useEffect, useState } from "react";
import {
  getFilloutForms,
  hideFilloutForm,
  showFilloutForm,
  purgeFilloutFormSubmissions,
  syncFilloutSubmissions,
  searchFilloutAlunni,
  getFilloutVisibleFields,
  saveFilloutVisibleFields,
  getFilloutSubmissions,
  getFilloutSubmission,
  updateFilloutSubmission,
  deleteFilloutSubmission,
  getFilloutEmailSettings,
  saveFilloutEmailSettings,
  sendFilloutConfirmation,
} from "@/lib/api";

const STATUS_COLORS = {
  pending: { bg: "#fff8e1", color: "#e65100", label: "In attesa", dot: "#29b6f6" },
  approved: { bg: "#e8f5e9", color: "#1b5e20", label: "Approvata", dot: "#2e7d32" },
  rejected: { bg: "#ffebee", color: "#c62828", label: "Rifiutata", dot: "#c62828" },
  archived: { bg: "#eceff1", color: "#455a64", label: "Archiviata", dot: "#90a4ae" },
};

function fmtDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function fmtDateShort(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(2);
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  } catch { return iso; }
}

export default function ModuliPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [visibleFields, setVisibleFields] = useState(null); // null = tutti visibili
  const [showFieldsConfig, setShowFieldsConfig] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [emailSettings, setEmailSettings] = useState(null);
  const [showEmailConfig, setShowEmailConfig] = useState(false);

  const loadForms = async (includeHidden = showHidden) => {
    setLoading(true);
    try {
      const r = await getFilloutForms(includeHidden);
      setForms(r.data.data || []);
      setHiddenCount(r.data.hidden_count || 0);
      const base = process.env.REACT_APP_BACKEND_URL || "";
      setWebhookUrl(`${base}${r.data.webhook_url || "/api/webhook/fillout"}`);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadForms(showHidden); /* eslint-disable-next-line */ }, [showHidden]);

  const toggleHidden = async (f, e) => {
    e.stopPropagation();
    try {
      if (f.is_hidden) {
        await showFilloutForm(f.form_id);
      } else {
        if (!window.confirm(`Nascondere il form "${f.name}" dal pannello?\n\nNOTA: il form NON viene cancellato da Fillout. Le submission già ricevute restano salvate e tornerai a vederle quando riveli i form nascosti.`)) return;
        await hideFilloutForm(f.form_id);
        if (selected?.form_id === f.form_id) { setSelected(null); setSubs([]); }
      }
      await loadForms(showHidden);
    } catch (err) { alert(err.response?.data?.detail || "Errore"); }
  };

  const purgeForm = async (f, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare TUTTE le ${f.submissions_total} submission del form "${f.name}"?\n\nATTENZIONE: operazione non reversibile. Il form Fillout non viene toccato.`)) return;
    try {
      await purgeFilloutFormSubmissions(f.form_id);
      if (selected?.form_id === f.form_id) setSubs([]);
      await loadForms(showHidden);
    } catch (err) { alert(err.response?.data?.detail || "Errore"); }
  };

  const loadSubs = async (formId, status = "") => {
    setSubsLoading(true);
    try {
      const r = await getFilloutSubmissions(formId, status || undefined);
      setSubs(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setSubsLoading(false); }
  };

  const selectForm = async (f) => {
    setSelected(f);
    setStatusFilter("");
    setShowFieldsConfig(false);
    setShowEmailConfig(false);
    // carica preferenze colonne
    try {
      const r = await getFilloutVisibleFields(f.form_id);
      setVisibleFields(r.data.visible_fields);
    } catch { setVisibleFields(null); }
    // carica email settings
    try {
      const r = await getFilloutEmailSettings(f.form_id);
      setEmailSettings(r.data);
    } catch { setEmailSettings(null); }
    loadSubs(f.form_id);
  };

  const saveEmailSettings = async (patch) => {
    if (!selected) return;
    try {
      const r = await saveFilloutEmailSettings(selected.form_id, patch);
      setEmailSettings(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const sendConfirmation = async (sub) => {
    try {
      await sendFilloutConfirmation(selected.form_id, sub.submission_id);
      alert("Email inviata correttamente");
      await loadSubs(selected.form_id, statusFilter);
      if (detail && detail.submission_id === sub.submission_id) {
        const r = await getFilloutSubmission(selected.form_id, sub.submission_id);
        setDetail(r.data);
      }
    } catch (e) { alert(e.response?.data?.detail || "Invio fallito"); }
  };

  const toggleFieldVisibility = async (key, allKeys) => {
    if (!selected) return;
    // Se visibleFields è null, è come se fossero tutti visibili
    const current = visibleFields === null ? [...allKeys] : [...visibleFields];
    const idx = current.indexOf(key);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(key);
    setVisibleFields(current);
    try {
      await saveFilloutVisibleFields(selected.form_id, current);
    } catch (e) { alert(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const resetFieldVisibility = async () => {
    if (!selected) return;
    setVisibleFields(null);
    try {
      await saveFilloutVisibleFields(selected.form_id, null);
    } catch (e) { alert(e.response?.data?.detail || "Errore reset"); }
  };

  const setFilter = (s) => {
    setStatusFilter(s);
    if (selected) loadSubs(selected.form_id, s);
  };

  const openDetail = async (sub) => {
    try {
      const r = await getFilloutSubmission(selected.form_id, sub.submission_id);
      setDetail(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const updateStatus = async (sub, newStatus) => {
    try {
      await updateFilloutSubmission(selected.form_id, sub.submission_id, { status: newStatus });
      await loadSubs(selected.form_id, statusFilter);
      if (detail && detail.submission_id === sub.submission_id) {
        const r = await getFilloutSubmission(selected.form_id, sub.submission_id);
        setDetail(r.data);
      }
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const saveNotes = async (notes) => {
    if (!detail) return;
    try {
      await updateFilloutSubmission(selected.form_id, detail.submission_id, { admin_notes: notes });
      setDetail({ ...detail, admin_notes: notes });
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const linkAlunno = async (codStr) => {
    if (!detail || !codStr) return;
    try {
      await updateFilloutSubmission(selected.form_id, detail.submission_id, { matched_alu_cod: codStr });
      const r = await getFilloutSubmission(selected.form_id, detail.submission_id);
      setDetail(r.data);
      loadSubs(selected.form_id, statusFilter);
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Eliminare la submission ${sub.submission_id}?`)) return;
    try {
      await deleteFilloutSubmission(selected.form_id, sub.submission_id);
      if (detail && detail.submission_id === sub.submission_id) setDetail(null);
      await loadSubs(selected.form_id, statusFilter);
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="moduli-page">
      <div className="sw-page-title">
        <h1>Moduli Fillout</h1>
        <div className="sw-breadcrumb">Home &gt; Moduli</div>
      </div>

      <div style={{ margin: "8px 20px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <button data-testid="btn-reload-forms" className="sw-filter-btn sw-filter-btn-primary" onClick={() => loadForms(showHidden)}>Aggiorna elenco form</button>
        <button
          data-testid="btn-sync-fillout"
          className="sw-filter-btn"
          style={{ background: "#1e7e34", color: "white", border: "none" }}
          onClick={async () => {
            try {
              const r = await syncFilloutSubmissions();
              alert(`Sync completata\nNuove: ${r.data.total_new}\nTotali processate: ${r.data.total_processed}`);
              await loadForms(showHidden);
              if (selected) loadSubs(selected.form_id, statusFilter);
            } catch (e) {
              alert(e.response?.data?.detail || "Errore sync");
            }
          }}
        >⟳ Sincronizza submission ora</button>
        <button data-testid="btn-toggle-setup" className="sw-filter-btn" onClick={() => setShowSetup(s => !s)}>
          {showSetup ? "Nascondi" : "Mostra"} configurazione webhook
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", marginLeft: 8 }}>
          <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} data-testid="toggle-hidden" />
          Mostra anche form nascosti {hiddenCount > 0 && <span style={{ background: "#888", color: "white", fontSize: 10, padding: "1px 6px", borderRadius: 2, fontWeight: 700 }}>{hiddenCount}</span>}
        </label>
        <span style={{ fontSize: 11, color: "#666", marginLeft: "auto" }}>
          Se i webhook non arrivano (piano base Fillout) usa "Sincronizza submission ora".
        </span>
      </div>

      {showSetup && (
        <div className="sw-card" style={{ margin: "4px 20px" }}>
          <div className="sw-card-header">Come configurare il webhook in Fillout</div>
          <div className="sw-card-body" style={{ fontSize: 12, lineHeight: 1.7 }}>
            Per ogni form (piano base) vai su <b>Integrations → HTTP webhook</b> e incolla questo URL:
            <div style={{ marginTop: 8, padding: 10, background: "#f4f9ff", border: "1px dashed #1565c0", fontFamily: "Consolas", fontSize: 11, wordBreak: "break-all" }} data-testid="webhook-url">
              {webhookUrl}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(webhookUrl); }} className="sw-filter-btn" style={{ marginTop: 6 }}>📋 Copia URL</button>
            <div style={{ marginTop: 10, color: "#555" }}>
              Ogni nuovo modulo che crei e pubblichi su Fillout, ti basterà incollare lo stesso URL nella sezione Integrations.
              canossaHUB crea <b>automaticamente una tabella separata</b> per ogni form alla prima submission.
            </div>
          </div>
        </div>
      )}

      <div style={{ margin: "8px 8px", display: "grid", gridTemplateColumns: "300px 1fr", gap: 10 }}>

        {/* Forms sidebar */}
        <div style={{ border: "1px solid #ccc", background: "white", maxHeight: "75vh", overflow: "auto" }}>
          <div style={{ padding: "8px 12px", background: "#f5f5f5", borderBottom: "1px solid #ccc", fontSize: 12, fontWeight: 700 }}>
            Form Fillout ({forms.length})
          </div>
          {loading ? <div className="sw-loading">Caricamento...</div> : forms.length === 0 ? (
            <div style={{ padding: 20, fontSize: 12, color: "#888", textAlign: "center" }}>Nessun form trovato</div>
          ) : forms.map(f => {
            const isActive = selected?.form_id === f.form_id;
            return (
              <div
                key={f.form_id}
                data-testid={`form-${f.form_id}`}
                onClick={() => !f.is_hidden && selectForm(f)}
                style={{
                  padding: "10px 12px",
                  borderBottom: "1px solid #eee",
                  cursor: f.is_hidden ? "default" : "pointer",
                  background: isActive ? "#fff3e0" : (f.is_hidden ? "#fafafa" : "white"),
                  opacity: f.is_hidden ? 0.55 : 1,
                  borderLeft: isActive ? "4px solid #ef6c00" : (f.is_hidden ? "4px solid #bbb" : "4px solid transparent"),
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ background: f.is_published ? "#1b5e20" : "#757575", color: "white", fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>
                    {f.is_published ? "PUB" : "BOZZA"}
                  </span>
                  {f.is_hidden && <span style={{ background: "#424242", color: "white", fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>NASCOSTO</span>}
                  <div style={{ fontWeight: 700, fontSize: 12, flex: 1 }}>{f.name || "(senza nome)"}</div>
                </div>
                <div style={{ fontSize: 10, color: "#666", marginTop: 4, display: "flex", gap: 10 }}>
                  <span>📥 {f.submissions_total} ricevute</span>
                  {f.submissions_pending > 0 && <span style={{ color: "#e65100", fontWeight: 700 }}>⏳ {f.submissions_pending} da rivedere</span>}
                </div>
                {f.last_received_at && <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>Ultima: {fmtDate(f.last_received_at)}</div>}
                <div style={{ fontSize: 9, color: "#aaa", fontFamily: "Consolas", marginTop: 2 }}>{f.form_id}</div>

                <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                  <button
                    data-testid={`btn-${f.is_hidden ? "show" : "hide"}-${f.form_id}`}
                    onClick={(e) => toggleHidden(f, e)}
                    title={f.is_hidden ? "Rendi nuovamente visibile" : "Nascondi dal pannello"}
                    style={{
                      border: `1px solid ${f.is_hidden ? "#1b5e20" : "#999"}`,
                      background: "white",
                      color: f.is_hidden ? "#1b5e20" : "#555",
                      padding: "2px 8px",
                      fontSize: 10,
                      cursor: "pointer",
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >{f.is_hidden ? "↺ Ripristina" : "🚫 Nascondi"}</button>
                  {f.submissions_total > 0 && (
                    <button
                      data-testid={`btn-purge-${f.form_id}`}
                      onClick={(e) => purgeForm(f, e)}
                      title="Elimina tutte le submission salvate per questo form"
                      style={{ border: "1px solid #c62828", background: "white", color: "#c62828", padding: "2px 8px", fontSize: 10, cursor: "pointer", borderRadius: 2, fontWeight: 600 }}
                    >🗑 Svuota ({f.submissions_total})</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Submissions panel */}
        <div style={{ border: "1px solid #ccc", background: "white" }}>
          {!selected ? (
            <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 12 }}>
              Seleziona un form dalla lista a sinistra
            </div>
          ) : (
            <>
              <div style={{ padding: "8px 12px", background: "#f5f5f5", borderBottom: "1px solid #ccc", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{selected.name}</div>
                <span style={{ fontSize: 10, color: "#666", fontFamily: "Consolas" }}>{selected.form_id}</span>
                <button
                  data-testid="btn-toggle-columns"
                  onClick={() => { setShowFieldsConfig(v => !v); setShowEmailConfig(false); }}
                  style={{
                    padding: "3px 10px",
                    border: "1px solid #1565c0",
                    background: showFieldsConfig ? "#1565c0" : "white",
                    color: showFieldsConfig ? "white" : "#1565c0",
                    fontSize: 10,
                    cursor: "pointer",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                  title="Scegli quali colonne del modulo vedere in lista"
                >⚙ Colonne{visibleFields !== null ? ` (${visibleFields.length})` : ""}</button>
                <button
                  data-testid="btn-toggle-email-config"
                  onClick={() => { setShowEmailConfig(v => !v); setShowFieldsConfig(false); }}
                  style={{
                    padding: "3px 10px",
                    border: `1px solid ${emailSettings?.enabled ? "#1b5e20" : "#6a1b9a"}`,
                    background: showEmailConfig ? (emailSettings?.enabled ? "#1b5e20" : "#6a1b9a") : "white",
                    color: showEmailConfig ? "white" : (emailSettings?.enabled ? "#1b5e20" : "#6a1b9a"),
                    fontSize: 10,
                    cursor: "pointer",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                  title="Configura email di conferma per questo modulo"
                >✉ Email{emailSettings?.enabled ? " ON" : " OFF"}{emailSettings?.auto_send_on_approve ? " · auto" : ""}</button>
                <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                  {[
                    { k: "", label: "Tutti", dot: null },
                    { k: "pending", label: "In attesa", dot: STATUS_COLORS.pending.dot },
                    { k: "approved", label: "Approvate", dot: STATUS_COLORS.approved.dot },
                    { k: "rejected", label: "Rifiutate", dot: STATUS_COLORS.rejected.dot },
                  ].map(opt => {
                    const active = statusFilter === opt.k;
                    return (
                      <button
                        key={opt.k}
                        data-testid={`filter-${opt.k || "all"}`}
                        onClick={() => setFilter(opt.k)}
                        style={{
                          padding: "3px 10px",
                          border: "1px solid #ccc",
                          fontSize: 10,
                          cursor: "pointer",
                          background: active ? "#0066cc" : "white",
                          color: active ? "white" : "#333",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        {opt.dot && (
                          <span style={{
                            display: "inline-block",
                            width: 8, height: 8,
                            borderRadius: "50%",
                            background: opt.dot,
                            border: active ? "1px solid rgba(255,255,255,0.7)" : "1px solid rgba(0,0,0,0.2)",
                          }} />
                        )}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ maxHeight: "70vh", overflow: "auto" }}>
                {subsLoading ? <div className="sw-loading">Caricamento...</div> : subs.length === 0 ? (
                  <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 12 }}>
                    Nessuna submission ancora. Configura il webhook o usa "Sincronizza submission ora".
                  </div>
                ) : (() => {
                  // Unione dinamica dei campi: prendo tutti i "questions" delle submission caricate
                  const allFields = [];
                  const seen = new Set();
                  for (const s of subs) {
                    for (const q of (s.questions || [])) {
                      const key = q.id || q.name || "";
                      if (key && !seen.has(key)) {
                        seen.add(key);
                        allFields.push({ key, name: q.name || key });
                      }
                    }
                  }
                  // Applica visibilità: se visibleFields è null => tutte; altrimenti solo quelle salvate
                  const isVisible = (key) => visibleFields === null ? true : visibleFields.includes(key);
                  const fieldOrder = allFields.filter(f => isVisible(f.key));
                  return (
                    <>
                      {showEmailConfig && emailSettings && (
                        <EmailConfigPanel
                          settings={emailSettings}
                          onChange={(patch) => setEmailSettings({ ...emailSettings, ...patch })}
                          onSave={saveEmailSettings}
                        />
                      )}
                      {showFieldsConfig && (
                        <div data-testid="columns-config-panel" style={{ background: "#fffbe8", border: "1px solid #e6c200", padding: "10px 14px", margin: "8px 8px 0 8px", borderRadius: 3 }}>
                          <div style={{ display: "flex", alignItems: "center", marginBottom: 6, gap: 10 }}>
                            <div style={{ fontWeight: 700, fontSize: 12 }}>Colonne visibili in lista ({fieldOrder.length}/{allFields.length})</div>
                            <button
                              data-testid="btn-columns-all"
                              onClick={() => resetFieldVisibility()}
                              style={{ padding: "2px 8px", border: "1px solid #1b5e20", background: "white", color: "#1b5e20", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                            >Mostra tutte</button>
                            <button
                              data-testid="btn-columns-none"
                              onClick={async () => { setVisibleFields([]); try { await saveFilloutVisibleFields(selected.form_id, []); } catch {} }}
                              style={{ padding: "2px 8px", border: "1px solid #c62828", background: "white", color: "#c62828", fontSize: 10, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
                            >Nascondi tutte</button>
                            <span style={{ marginLeft: "auto", fontSize: 10, color: "#666" }}>Click su un campo per mostrarlo / nasconderlo</span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {allFields.map(f => {
                              const on = isVisible(f.key);
                              return (
                                <label
                                  key={f.key}
                                  data-testid={`col-chk-${f.key}`}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "3px 8px",
                                    border: `1px solid ${on ? "#1565c0" : "#bbb"}`,
                                    background: on ? "#e3f2fd" : "#f5f5f5",
                                    color: on ? "#0d47a1" : "#777",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    userSelect: "none",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={on}
                                    onChange={() => toggleFieldVisibility(f.key, allFields.map(a => a.key))}
                                    style={{ margin: 0 }}
                                  />
                                  {f.name}
                                </label>
                              );
                            })}
                            {allFields.length === 0 && <span style={{ fontSize: 11, color: "#888" }}>Nessun campo rilevato nelle submission.</span>}
                          </div>
                        </div>
                      )}
                      <table className="sw-table sw-table-compact" style={{ fontSize: 10, tableLayout: "auto", width: "100%" }}>
                      <thead style={{ position: "sticky", top: 0, background: "#e8e8e8", zIndex: 2 }}>
                        <tr>
                          <th style={{ width: 90, position: "sticky", left: 0, background: "#e8e8e8", zIndex: 3 }}>Ricevuta</th>
                          <th style={{ width: 38, textAlign: "center" }} title="Stato">●</th>
                          <th style={{ width: 170 }}>Alunno</th>
                          <th style={{ width: 28, textAlign: "center" }} title="Allegati">📎</th>
                          {fieldOrder.map(f => (
                            <th key={f.key} style={{ maxWidth: 180 }} title={f.name}>{f.name}</th>
                          ))}
                          <th style={{ width: 80 }}>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subs.map(s => {
                          const st = STATUS_COLORS[s.status] || STATUS_COLORS.pending;
                          const files = s.file_attachments?.length || 0;
                          // Build fast lookup for this submission
                          const qMap = {};
                          for (const q of (s.questions || [])) {
                            const key = q.id || q.name || "";
                            if (key) qMap[key] = q.value;
                          }
                          return (
                            <tr key={s.submission_id} data-testid={`sub-${s.submission_id}`} onClick={() => openDetail(s)} style={{ cursor: "pointer" }}>
                              <td style={{ position: "sticky", left: 0, background: "white", borderRight: "1px solid #ddd", whiteSpace: "nowrap", fontSize: 10 }} title={fmtDate(s.received_at)}>{fmtDateShort(s.received_at)}</td>
                              <td style={{ textAlign: "center" }} title={st.label}>
                                <span style={{
                                  display: "inline-block",
                                  width: 12, height: 12,
                                  borderRadius: "50%",
                                  background: st.dot,
                                  border: "1px solid rgba(0,0,0,0.15)",
                                  verticalAlign: "middle",
                                }} />
                              </td>
                              <td style={{ fontSize: 10 }}>
                                {s.matched_alu ? (
                                  <div>
                                    <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 }}>
                                      <span style={{ fontWeight: 600 }}>{s.matched_alu.nome_completo}</span>
                                    </div>
                                    <div style={{ marginTop: 1, display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
                                      <span style={{ color: "#888", fontSize: 9 }}>#{s.matched_alu.Cod}</span>
                                      {s.matched_alu.classe && (
                                        <span style={{ background: "#e3f2fd", color: "#0d47a1", fontSize: 9, padding: "0 4px", borderRadius: 2, fontWeight: 700 }} title="Classe">
                                          {s.matched_alu.classe}
                                        </span>
                                      )}
                                      {s.matched_alu.settore && (
                                        <span style={{ background: "#f3e5f5", color: "#4a148c", fontSize: 9, padding: "0 4px", borderRadius: 2, fontWeight: 700 }} title="Settore">
                                          {s.matched_alu.settore}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span style={{ color: "#e65100", fontStyle: "italic" }}>Non collegato</span>
                                )}
                              </td>
                              <td className="center-col" style={{ fontSize: 10 }}>{files > 0 ? files : ""}</td>
                              {fieldOrder.map(f => (
                                <td key={f.key} style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10 }} title={formatCellValue(qMap[f.key])}>
                                  {formatCellValue(qMap[f.key])}
                                </td>
                              ))}
                              <td onClick={e => e.stopPropagation()} style={{ whiteSpace: "nowrap", textAlign: "center" }}>
                                {s.status !== "approved" && <button onClick={() => updateStatus(s, "approved")} title="Approva" style={{ border: "none", background: "transparent", color: "#1b5e20", padding: "0 3px", fontSize: 13, cursor: "pointer", fontWeight: 700 }}>✓</button>}
                                {s.status !== "rejected" && <button onClick={() => updateStatus(s, "rejected")} title="Rifiuta" style={{ border: "none", background: "transparent", color: "#c62828", padding: "0 3px", fontSize: 13, cursor: "pointer", fontWeight: 700 }}>✕</button>}
                                {s.email_last_status === "sent" && <span title={`Email inviata il ${fmtDate(s.email_last_sent_at)}`} style={{ fontSize: 11, color: "#1b5e20", marginLeft: 2 }}>✉</span>}
                                {s.email_last_status === "failed" && <span title={`Invio fallito: ${s.email_last_error || ""}`} style={{ fontSize: 11, color: "#c62828", marginLeft: 2 }}>✉!</span>}
                                <button onClick={() => remove(s)} style={{ border: "none", background: "transparent", color: "#999", padding: "0 3px", fontSize: 12, cursor: "pointer" }} title="Elimina">🗑</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>

      {detail && (
        <DetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          onStatusChange={(s) => updateStatus(detail, s)}
          onSaveNotes={saveNotes}
          onLinkAlunno={linkAlunno}
          onSendEmail={() => sendConfirmation(detail)}
          emailEnabled={emailSettings?.enabled}
        />
      )}
    </div>
  );
}

function DetailModal({ detail, onClose, onStatusChange, onSaveNotes, onLinkAlunno, onSendEmail, emailEnabled }) {
  const [notes, setNotes] = useState(detail?.admin_notes || "");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const st = STATUS_COLORS[detail.status] || STATUS_COLORS.pending;

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await searchFilloutAlunni(query.trim());
        setResults(r.data.data || []);
      } catch (e) { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const link = (cod) => {
    onLinkAlunno(String(cod));
    setQuery("");
    setResults([]);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "white", width: "94%", maxWidth: 900, maxHeight: "92vh", display: "flex", flexDirection: "column", border: "1px solid #999", borderRadius: 4 }}>
        <div style={{ background: "#ef6c00", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            Submission <span style={{ fontFamily: "Consolas" }}>{detail.submission_id}</span>
            <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 400, opacity: 0.9 }}>{fmtDate(detail.received_at)}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 20, fontWeight: 700 }}>✕</button>
        </div>

        <div style={{ padding: 16, overflow: "auto", flex: 1 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ background: st.bg, color: st.color, padding: "3px 12px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>{st.label}</span>
            <button onClick={() => onStatusChange("approved")} disabled={detail.status === "approved"} style={{ background: "#2e7d32", color: "white", border: "none", padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", opacity: detail.status === "approved" ? 0.5 : 1 }}>✓ Approva</button>
            <button onClick={() => onStatusChange("rejected")} disabled={detail.status === "rejected"} style={{ background: "#c62828", color: "white", border: "none", padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer", opacity: detail.status === "rejected" ? 0.5 : 1 }}>✕ Rifiuta</button>
            <button onClick={() => onStatusChange("archived")} style={{ background: "#555", color: "white", border: "none", padding: "5px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Archivia</button>
          </div>

          <div style={{ marginBottom: 14, padding: 10, background: "#f4f9ff", border: "1px solid #cce" }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Alunno collegato:</div>
            {detail.matched_alu ? (
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {detail.matched_alu.nome_completo} <span style={{ color: "#666", fontWeight: 400 }}>(cod. {detail.matched_alu.Cod})</span>
                {detail.matched_alu.classe && (
                  <span style={{ marginLeft: 8, background: "#e3f2fd", color: "#0d47a1", fontSize: 10, padding: "2px 8px", borderRadius: 2, fontWeight: 700 }} title="Classe anno corrente">
                    Classe {detail.matched_alu.classe}
                  </span>
                )}
                {detail.matched_alu.settore && (
                  <span style={{ marginLeft: 4, background: "#f3e5f5", color: "#4a148c", fontSize: 10, padding: "2px 8px", borderRadius: 2, fontWeight: 700 }} title="Settore">
                    Set. {detail.matched_alu.settore}
                  </span>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#e65100" }}>
                Non collegato automaticamente.
                {detail.cognome_raw && <span style={{ color: "#666", marginLeft: 6 }}>(dal form: <b>{detail.cognome_raw} {detail.nome_raw || ""}</b>)</span>}
              </div>
            )}
            <div style={{ marginTop: 8, position: "relative" }}>
              <input
                type="text"
                placeholder="Cerca per cognome, nome o codice..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                data-testid="link-search-input"
                style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }}
              />
              {query.trim().length >= 2 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ccc", borderTop: "none", maxHeight: 220, overflow: "auto", zIndex: 10, boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}>
                  {searching ? (
                    <div style={{ padding: 10, fontSize: 11, color: "#888" }}>Cerco...</div>
                  ) : results.length === 0 ? (
                    <div style={{ padding: 10, fontSize: 11, color: "#888" }}>Nessun alunno trovato</div>
                  ) : results.map(a => (
                    <div
                      key={a.Cod}
                      data-testid={`link-result-${a.Cod}`}
                      onClick={() => link(a.Cod)}
                      style={{ padding: "6px 10px", borderBottom: "1px solid #eee", cursor: "pointer", fontSize: 12 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#e3f2fd"}
                      onMouseLeave={e => e.currentTarget.style.background = "white"}
                    >
                      <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{a.Cog} {a.Nom}</span>
                        {a.classe && <span style={{ background: "#e3f2fd", color: "#0d47a1", fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>{a.classe}</span>}
                        {a.settore && <span style={{ background: "#f3e5f5", color: "#4a148c", fontSize: 9, padding: "1px 5px", borderRadius: 2, fontWeight: 700 }}>{a.settore}</span>}
                      </div>
                      <div style={{ fontSize: 10, color: "#666" }}>
                        <span style={{ fontFamily: "Consolas" }}>cod. {a.Cod}</span>
                        {a.DataNascita && <span style={{ marginLeft: 10 }}>nato il {a.DataNascita}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <EmailSection detail={detail} onSendEmail={onSendEmail} emailEnabled={emailEnabled} />

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Risposte del modulo</div>
            <table className="sw-table" style={{ fontSize: 11 }}>
              <thead><tr><th style={{ width: "40%" }}>Domanda</th><th>Risposta</th></tr></thead>
              <tbody>
                {(detail.questions || []).map((q, i) => (
                  <tr key={i}><td>{q.name}</td><td>{renderValue(q.value)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          {detail.file_attachments?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>📎 Allegati ({detail.file_attachments.length})</div>
              <ul style={{ fontSize: 12, paddingLeft: 20 }}>
                {detail.file_attachments.map((f, i) => (
                  <li key={i}>
                    <a href={f.url} target="_blank" rel="noreferrer" style={{ color: "#0066cc" }}>{f.name || f.url}</a>
                    {f.size && <span style={{ color: "#888", marginLeft: 6 }}>({(f.size / 1024).toFixed(1)} KB)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Note interne</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} style={{ width: "100%", minHeight: 80, padding: 6, border: "1px solid #ccc", fontSize: 12, fontFamily: "inherit" }} placeholder="Note visibili solo agli operatori..." />
            <button onClick={() => onSaveNotes(notes)} className="sw-filter-btn sw-filter-btn-primary" style={{ marginTop: 4, fontSize: 11 }}>Salva note</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCellValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (Array.isArray(v)) {
    return v.map(x => {
      if (x && typeof x === "object") return x.name || x.label || x.value || x.url || JSON.stringify(x);
      return String(x);
    }).join(", ");
  }
  if (typeof v === "object") return v.name || v.label || v.value || v.url || JSON.stringify(v);
  return String(v);
}

function renderValue(v) {
  if (v === null || v === undefined || v === "") return <span style={{ color: "#ccc" }}>—</span>;
  if (Array.isArray(v)) return v.map(x => typeof x === "object" ? JSON.stringify(x) : String(x)).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function EmailConfigPanel({ settings, onChange, onSave }) {
  const [localSubject, setLocalSubject] = useState(settings.subject_template);
  const [localBody, setLocalBody] = useState(settings.body_template);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSubject(settings.subject_template);
    setLocalBody(settings.body_template);
  }, [settings.form_id, settings.subject_template, settings.body_template]);

  const save = async () => {
    setSaving(true);
    await onSave({ subject_template: localSubject, body_template: localBody });
    setSaving(false);
  };

  return (
    <div data-testid="email-config-panel" style={{ background: "#f3e5f5", border: "1px solid #ba68c8", padding: "10px 14px", margin: "8px 8px 0 8px", borderRadius: 3 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: "#4a148c" }}>✉ Email di conferma per questo modulo</div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer" }}>
          <input
            type="checkbox"
            data-testid="email-enabled"
            checked={!!settings.enabled}
            onChange={e => { onChange({ enabled: e.target.checked }); onSave({ enabled: e.target.checked }); }}
          />
          <b>Abilita invio email per questo modulo</b>
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, cursor: settings.enabled ? "pointer" : "not-allowed", opacity: settings.enabled ? 1 : 0.5 }}>
          <input
            type="checkbox"
            data-testid="email-auto-send"
            disabled={!settings.enabled}
            checked={!!settings.auto_send_on_approve}
            onChange={e => { onChange({ auto_send_on_approve: e.target.checked }); onSave({ auto_send_on_approve: e.target.checked }); }}
          />
          Invia automaticamente all&apos;approvazione
        </label>
      </div>

      <div style={{ fontSize: 10, color: "#555", marginBottom: 6, lineHeight: 1.5 }}>
        Placeholder disponibili: <code>{"{nome}"}</code> <code>{"{cognome}"}</code> <code>{"{nome_completo}"}</code> <code>{"{classe}"}</code> <code>{"{settore}"}</code> <code>{"{nome_form}"}</code> <code>{"{submission_id}"}</code> <code>{"{data_invio}"}</code> · per una risposta specifica del modulo: <code>{"{q:Nome esatto del campo}"}</code>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>Oggetto</div>
        <input
          type="text"
          data-testid="email-subject-input"
          value={localSubject}
          onChange={e => setLocalSubject(e.target.value)}
          style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }}
        />
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 3 }}>Corpo email (HTML)</div>
        <textarea
          data-testid="email-body-input"
          value={localBody}
          onChange={e => setLocalBody(e.target.value)}
          style={{ width: "100%", minHeight: 180, padding: 6, border: "1px solid #ccc", fontSize: 11, fontFamily: "Consolas, monospace", boxSizing: "border-box" }}
        />
      </div>

      <button
        data-testid="btn-save-email-templates"
        onClick={save}
        disabled={saving}
        style={{ padding: "5px 14px", border: "none", background: "#6a1b9a", color: "white", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}
      >{saving ? "Salvo..." : "Salva oggetto & corpo"}</button>
    </div>
  );
}

function EmailSection({ detail, onSendEmail, emailEnabled }) {
  const [sending, setSending] = useState(false);
  const logs = Array.isArray(detail.email_logs) ? detail.email_logs : [];
  const lastStatus = detail.email_last_status;
  const doSend = async () => {
    setSending(true);
    try { await onSendEmail(); } finally { setSending(false); }
  };
  return (
    <div style={{ marginBottom: 14, padding: 10, background: "#fafafa", border: "1px solid #ddd" }} data-testid="email-section">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 700 }}>✉ Email di conferma</div>
        {!emailEnabled && (
          <span style={{ fontSize: 10, color: "#c62828" }}>Invio disabilitato per questo modulo — abilita nel pulsante "✉ Email" in alto.</span>
        )}
        {lastStatus === "sent" && <span style={{ background: "#e8f5e9", color: "#1b5e20", fontSize: 10, padding: "2px 8px", borderRadius: 2, fontWeight: 700 }}>INVIATA</span>}
        {lastStatus === "failed" && <span style={{ background: "#ffebee", color: "#c62828", fontSize: 10, padding: "2px 8px", borderRadius: 2, fontWeight: 700 }}>ERRORE</span>}
        <button
          data-testid="btn-send-email"
          onClick={doSend}
          disabled={sending || !emailEnabled}
          style={{
            marginLeft: "auto",
            padding: "5px 14px",
            background: sending ? "#999" : "#6a1b9a",
            color: "white",
            border: "none",
            fontSize: 11,
            fontWeight: 700,
            cursor: sending || !emailEnabled ? "not-allowed" : "pointer",
            opacity: emailEnabled ? 1 : 0.5,
            borderRadius: 2,
          }}
        >{sending ? "Invio..." : (logs.some(l => l.status === "sent") ? "Reinvia conferma" : "Invia conferma")}</button>
      </div>

      {logs.length === 0 ? (
        <div style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>Nessun invio effettuato.</div>
      ) : (
        <table className="sw-table" style={{ fontSize: 10 }}>
          <thead>
            <tr>
              <th style={{ width: 130 }}>Data/ora</th>
              <th style={{ width: 70 }}>Stato</th>
              <th>Destinatario</th>
              <th>Oggetto</th>
              <th style={{ width: 80 }}>Trigger</th>
              <th>Errore</th>
            </tr>
          </thead>
          <tbody>
            {[...logs].reverse().map((l, i) => {
              const isOk = l.status === "sent";
              const color = isOk ? "#1b5e20" : (l.status === "failed" ? "#c62828" : "#888");
              return (
                <tr key={i}>
                  <td style={{ fontSize: 10 }}>{fmtDate(l.sent_at)}</td>
                  <td><span style={{ color, fontWeight: 700, fontSize: 10 }}>{l.status?.toUpperCase() || "?"}</span></td>
                  <td style={{ fontSize: 10 }}>{l.to || "-"}</td>
                  <td style={{ fontSize: 10, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.subject}>{l.subject || "-"}</td>
                  <td style={{ fontSize: 10 }}>{l.trigger === "auto_approve" ? "auto" : (l.trigger || "manual")}</td>
                  <td style={{ fontSize: 10, color: "#c62828", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.error}>{l.error || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

